import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
    ViewChild
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import {
    ButtonComponent,
    ColumnType,
    Page,
    PaginatorComponent,
    PaginatorVariant,
    ServerDataSource,
    TableComponent,
    TableFormat
} from "@sxm-content-creators/tools-ds";
import { map } from "rxjs";
import { ThemesApiService } from "../../../themes/services/themes.api.service";
import { TenantsApiService } from "../../../../shared/services/tenants.api.service";
import { TenantResponse } from "../../models/tenant.model";

@Component({
    selector: "app-tenants",
    templateUrl: "./tenants.component.html",
    styleUrl: "./tenants.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ButtonComponent, RouterLink, TableComponent, PaginatorComponent]
})
export class TenantsComponent implements OnInit, AfterViewInit {
    private readonly tenantsService = inject(TenantsApiService);
    private readonly themesService = inject(ThemesApiService);
    private readonly destroyRef = inject(DestroyRef);

    @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;

    readonly paginatorVariant = PaginatorVariant.CURRENT;
    readonly themeNameMap = signal(new Map<string, string>());

    private readonly cursorMap = new Map<number, string>();

    /** Set once a load resolves, so the empty state can't flash while the first page is in flight. */
    private readonly hasLoaded = signal(false);
    private readonly isEmpty = signal(false);

    /** Swaps the table + paginator for the "No Tenants Yet" panel. */
    readonly showEmptyState = computed(() => this.hasLoaded() && this.isEmpty());

    readonly dataSource = new ServerDataSource<TenantResponse>((page: Page<TenantResponse>) => this.loadTenants(page), {
        active: "",
        direction: ""
    });

    readonly tableFormat: TableFormat = {
        headers: [{ name: "" }, { name: "Tenant Name" }, { name: "Theme Name" }, { name: "" }],
        columns: [
            { type: ColumnType.CUSTOM, display: "image" },
            { type: ColumnType.TEXT, firstLine: "name", display: "name" },
            { type: ColumnType.CUSTOM, display: "theme-name", showChildTemplate: "theme-name" },
            { type: ColumnType.CUSTOM, display: "edit-action", showChildTemplate: "edit-action" }
        ]
    };

    ngOnInit(): void {
        this.themesService
            .getAllThemes()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                this.themeNameMap.set(new Map(response.items.map(t => [t.id, t.name])));
            });
    }

    // The paginator is removed from the DOM while the empty state shows, so both accesses are
    // guarded: an empty list has nothing to page through anyway.
    ngAfterViewInit(): void {
        this.dataSource.count().subscribe(count => {
            if (this.paginator) {
                this.paginator.length = count;
            }
        });

        this.paginator?.page.subscribe(event => {
            this.dataSource.pageChange(event);
        });
    }

    // Currently cursor pagination isn't supported by the tools-ds table so have to work around that a bit here.
    // Jira for adding support for it is https://jira.savagebeast.com/browse/CREATORS-26602
    private loadTenants(page: Page<TenantResponse>) {
        const cursor = this.cursorMap.get(page.index) ?? null;
        return this.tenantsService.getTenants(page.size, cursor).pipe(
            map(response => {
                if (response.nextCursor) {
                    this.cursorMap.set(page.index + 1, response.nextCursor);
                }
                // Only the first page coming back empty means there are no tenants at all — under
                // cursor pagination a later page can't be empty, so it must not blank the table.
                if (page.index === 0) {
                    this.isEmpty.set(response.items.length === 0);
                }
                this.hasLoaded.set(true);
                return {
                    data: response.items,
                    count: response.hasNext ? Number.MAX_SAFE_INTEGER : response.items.length
                };
            })
        );
    }
}
